import { type ReactNode, useState } from "react";
import type { Messages } from "@/lib/i18n";

/**
 * Interactive "release configuration" mockup — firmware Live / Mandatory / Reset
 * toggles plus a staged-rollout slider. Shown on the OTA solution page and
 * reused as the lead visual on the `/solutions` hub. Renders statically during
 * SSR (default state) and becomes interactive after hydration.
 */
export function RolloutControlsPanel({ messages }: { messages: Messages }) {
  const [live, setLive] = useState(true);
  const [mandatory, setMandatory] = useState(true);
  const [resetAfterUpdate, setResetAfterUpdate] = useState(false);
  const [rollout, setRollout] = useState(35);
  const estimatedDevices = Math.round((612 * rollout) / 100);
  const toggles = [
    { title: messages.otaLiveLabel, body: messages.otaUiLiveBody, enabled: live, set: setLive },
    {
      title: messages.otaUiMandatoryTitle,
      body: messages.otaUiMandatoryBody,
      enabled: mandatory,
      set: setMandatory,
    },
    {
      title: messages.otaUiResetTitle,
      body: messages.otaUiResetBody,
      enabled: resetAfterUpdate,
      set: setResetAfterUpdate,
    },
  ];

  return (
    <figure className="relative" aria-label={messages.otaMonitorTitle}>
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <AppWindowFrame messages={messages} path="alcatraz.inext.ltd / releases / FW v1.9.1">
        <div className="border-b border-border/60 px-5 py-4 text-sm font-semibold text-foreground sm:px-6">
          <span>TV5 Ultra</span>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-muted-foreground">cdh313a:yourisp</span>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-primary">FW v1.9.1</span>
        </div>

        <div className="divide-y divide-border/60 px-5 sm:px-6">
          {toggles.map(({ title, body, enabled, set }) => (
            <div
              key={title}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-4"
            >
              <div>
                <span className="block text-sm font-semibold text-foreground">{title}</span>
                <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                  {body}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={title}
                onClick={() => set(!enabled)}
                className={`relative block h-7 w-12 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  enabled ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                    enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          ))}

          <div className="py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="block text-sm font-semibold text-foreground">
                  {messages.otaUiStagedTitle}
                </span>
                <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                  {messages.otaUiStagedBody}
                </span>
              </div>
              <div className="grid h-10 min-w-14 place-items-center rounded-md border border-border bg-background px-3 text-sm font-medium tabular-nums text-foreground">
                {rollout}%
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="100"
              value={rollout}
              onChange={(event) => setRollout(Number(event.target.value))}
              aria-label={messages.otaUiStagedTitle}
              className="mt-5 h-2 w-full cursor-pointer accent-primary"
            />
            <p
              className="mt-3 flex items-center gap-2 text-xs font-medium text-muted-foreground"
              aria-live="polite"
            >
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${live ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
              />
              <span>
                {rollout}% · {messages.otaUiApproximately} {estimatedDevices}{" "}
                {messages.otaUiEligibleDevices}
              </span>
            </p>
          </div>
        </div>
      </AppWindowFrame>
    </figure>
  );
}

export function AppWindowFrame({
  messages,
  path,
  children,
}: {
  messages: Messages;
  path: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-xl shadow-primary/10">
      <div className="flex items-center gap-3 border-b border-border/60 bg-muted/45 px-4 py-3">
        <span className="flex shrink-0 gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
        </span>
        <span className="min-w-0 flex-1 truncate rounded-md border border-border/60 bg-background/80 px-3 py-1.5 text-center font-mono text-[10px] text-muted-foreground sm:text-xs">
          {path}
        </span>
        <span className="hidden shrink-0 rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-foreground/90 sm:inline">
          {messages.otaUiInteractiveLabel}
        </span>
      </div>
      {children}
    </div>
  );
}
